import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import withTitle from "../withTitle";
import withProps from "../withProps";

import { WellKnownGroups, IAuthProps } from "../AuthContext";
import { V2 } from "../../Api";
import DelayInput from "../DelayInput";
import { formatName, formatDateTimeLocal, truncateEllipse, formatList } from "../../Utilities";
import { DateTime } from "luxon";
import withAuth from "../withAuth";

interface IOutreachOnCallViewState {
    isLoading: boolean | string;

    searchText: string;
    searchOffset: number;

    list: any[];

    isOnCall: boolean;
    onCallName: string;
}

interface IOutreachOnCallViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
    group: string;
}

const PAGE_SIZE = 50;

class OutreachOnCallView extends React.Component<IOutreachOnCallViewProps, IOutreachOnCallViewState> {
    private _refreshTimer?: number;

    constructor(props: Readonly<IOutreachOnCallViewProps>) {
        super(props);

        const state: IOutreachOnCallViewState = {
            isLoading: true,

            searchText: "",
            searchOffset: 0,

            list: [],

            isOnCall: false,
            onCallName: "",
        };

        if (props.location.state) {
            const pState = props.location.state;

            state.searchText = pState.searchText || "";
            state.searchOffset = pState.searchOffset || 0;
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.loadData = this.loadData.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
    }

    public componentDidMount() {
        this.loadData();

        this._refreshTimer = window.setInterval(() => this.loadData(false), 30000);
    }

    public componentWillUnmount() {
        if (this._refreshTimer) {
            window.clearInterval(this._refreshTimer);
            this._refreshTimer = undefined;
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                {
                    !this.state.isLoading
                        ? this.state.isOnCall
                            ? <div className="status-box slim">You are on call</div>
                            : <div className="orange-box slim">{this.state.onCallName} on call</div>
                        : null
                }
                <div className="filter-control-box">
                    <DelayInput type="search" timeout={500} className="fill-space" name="searchText" value={this.state.searchText} placeholder="Search..." onChangeDelayed={this.handleElementChange} />
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Date & Time</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>On-Call</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading === true
                                ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                                : typeof this.state.isLoading === "string"
                                    ? <tr><td colSpan={10}><span className="error-text">{this.state.isLoading}</span></td></tr>
                                    : this.state.list.length === 0
                                        ? <tr><td colSpan={10}>No results found</td></tr>
                                        : this.state.list.map((a, i) => (
                                            <tr className="clickable" key={a.id} tabIndex={0} onClick={() => this.goSurvey(a.questionnaireId)}>
                                                <td className="inline" data-caption="Name: ">{formatName(a.name)}</td>
                                                <td className="inline" data-caption="Date & Time: ">{formatDateTimeLocal(a.timestamp)}</td>
                                                <td className="col-wrap" data-caption="Note: ">{truncateEllipse(a.note || "", 50)}</td>
                                            </tr>
                                        ))}
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.list.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData(showSpinner = true) {
        try {
            if (showSpinner) {
                this.setState({
                    isLoading: true
                });
            }

            this.saveSearchState();

            const parameters = [
                `offset=${this.state.searchOffset}`,
                `limit=${PAGE_SIZE}`,
            ];

            if (this.state.searchText) {
                parameters.push(`q=${encodeURIComponent(this.state.searchText)}`);
            }

            const [list, onCall] = await Promise.all([
                V2.get<any[]>(`survey/outreach/on-call/${this.props.group}?${parameters.join("&")}`),
                V2.get<any[]>(`schedule/coverage/status/${this.props.group}/${DateTime.local().toISODate()}`)
            ]);

            this.setState({
                isLoading: false,

                list,

                isOnCall: onCall.some(a => a.employee?.id === this.props.authContext.user.employee.id),
                onCallName: formatList(onCall.map(a => formatName(a.employee.name))) + (onCall.length === 1 ? " is" : " are")
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load statements"
            });
        }
    }

    private saveSearchState() {
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchOffset: this.state.searchOffset,
            searchText: this.state.searchText,

        };
        this.props.history.replace(currentLocation);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.loadData());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.loadData());
    }

    private goSurvey(id: number) {
        this.props.history.push(`${this.props.location.pathname}/survey/${id}`);
    }
}

export const OutreachSurgicalOnCallView = withTitle("On-Call List", withAuth(withProps(OutreachOnCallView, { group: WellKnownGroups.SurgicalConsultant })));
export const OutreachTelemarketerOnCallView = withTitle("On-Call List", withAuth(withProps(OutreachOnCallView, { group: WellKnownGroups.PatientCareRep })));
